import { Injectable, inject } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { IndexedDbService } from './indexed-db.service';
import { SELECTED_ORG_TOKEN, BASE_ORG_TOKEN } from '../../tokens/selected-org.token';
import { CryptoEngineService } from './crypto-engine.service';

@Injectable({
  providedIn: 'root',
})
export class VaultStateService {
  private _masterKey: CryptoKey | null = null;
  private _searchSalt: CryptoKey | null = null;

  public get masterKey() { return this._masterKey; }
  public get searchSalt() { return this._searchSalt; }

  private lockedSubject = new BehaviorSubject<boolean>(false);
  public locked$ = this.lockedSubject.asObservable();

  private showVaultSetupSubject = new BehaviorSubject<boolean>(false);
  public showVaultSetup$ = this.showVaultSetupSubject.asObservable();

  private selectedOrg = inject(SELECTED_ORG_TOKEN);
  private baseOrgSubject = inject(BASE_ORG_TOKEN);
  private currentOrgId: string | null = null;
  private isCurrentOrgRoot = false;
  private cryptoEngine = inject(CryptoEngineService);
  private indexedDbService = inject(IndexedDbService);

  public get isBypassVault(): boolean {
    return this.isCurrentOrgRoot || this.baseOrgSubject.value !== null;
  }

  constructor() {
    this.selectedOrg.subscribe((org) => {
      if (org) {
        this.isCurrentOrgRoot = org.type === 'root' || org.orgType === 'root';
        if (this.currentOrgId && this.currentOrgId !== org.id) {
          this.lockVault(false); // Do not force PIN UI on org change
        }
        this.currentOrgId = org.id;
      } else {
        this.isCurrentOrgRoot = false;
        if (this.currentOrgId !== null) {
          this.lockVault(false); // Silently lock if no org is selected
        }
        this.currentOrgId = null;
      }
    });

    this.tryRestoreSession();
  }

  // --- Vault Verification (Organization Tag) ---

  async createVerificationTag(mnemonic: string): Promise<string> {
    const keys = await this.cryptoEngine.deriveKeysFromMnemonic(mnemonic);
    const masterKey = await this.cryptoEngine.importMasterKey(keys.masterKeyBytes, true);

    const iv = window.crypto.getRandomValues(new Uint8Array(12));
    const encoded = this.cryptoEngine.encoder.encode('Medease-Verify');

    const ciphertext = await window.crypto.subtle.encrypt(
      { name: 'AES-GCM', iv: iv },
      masterKey,
      encoded,
    );

    const resultBuffer = new Uint8Array(iv.byteLength + ciphertext.byteLength);
    resultBuffer.set(iv);
    resultBuffer.set(new Uint8Array(ciphertext), iv.byteLength);
    return this.cryptoEngine.arrayBufferToBase64(resultBuffer);
  }

  async verifyExistingTag(
    mnemonic: string,
    encryptedTag: string,
  ): Promise<boolean> {
    try {
      const keys = await this.cryptoEngine.deriveKeysFromMnemonic(mnemonic);
      const masterKey = await this.cryptoEngine.importMasterKey(keys.masterKeyBytes, false);

      const resultBuffer = this.cryptoEngine.base64ToArrayBuffer(encryptedTag);
      const iv = resultBuffer.slice(0, 12);
      const ciphertext = resultBuffer.slice(12);

      const decryptedData = await window.crypto.subtle.decrypt(
        { name: 'AES-GCM', iv: iv },
        masterKey,
        ciphertext,
      );

      const result = this.cryptoEngine.decoder.decode(decryptedData);
      return result === 'Medease-Verify';
    } catch (e) {
      console.warn('Vault verification failed', e);
      return false;
    }
  }

  // --- Vault Setup ---

  async setupVault(mnemonic: string, pin: string): Promise<void> {
    const keys = await this.cryptoEngine.deriveKeysFromMnemonic(mnemonic);

    const keyMaterialToWrap = await window.crypto.subtle.importKey(
      'raw',
      keys.combinedBytes as any,
      { name: 'HMAC', hash: 'SHA-512' },
      true, 
      ['sign'], 
    );

    const salt = window.crypto.getRandomValues(new Uint8Array(16));
    const wrappingKey = await this.cryptoEngine.deriveWrappingKey(pin, salt.buffer);

    const wrappedKey = await window.crypto.subtle.wrapKey(
      'raw',
      keyMaterialToWrap,
      wrappingKey,
      'AES-KW',
    );

    this._masterKey = await this.cryptoEngine.importMasterKey(keys.masterKeyBytes, false);
    this._searchSalt = await this.cryptoEngine.importSearchSalt(keys.searchSaltBytes);

    if (!this.currentOrgId) throw new Error('No organization selected');
    await this.indexedDbService.saveWrappedKey(
      this.currentOrgId,
      wrappedKey,
      salt.buffer,
    );

    await this.persistSession(keys.combinedBytes);
  }

  // --- Unlock & Restore ---

  async changePin(oldPin: string, newPin: string): Promise<boolean> {
    if (!this.currentOrgId) return false;
    const data = await this.indexedDbService.getWrappedKeyData(this.currentOrgId);
    if (!data) return false;

    const { wrappedKey, salt } = data;

    try {
      const oldWrappingKey = await this.cryptoEngine.deriveWrappingKey(oldPin, salt);
      const combinedKeyHandle = await window.crypto.subtle.unwrapKey(
        'raw',
        wrappedKey,
        oldWrappingKey,
        'AES-KW',
        { name: 'HMAC', hash: 'SHA-512' },
        true,
        ['sign'],
      );

      const newSalt = window.crypto.getRandomValues(new Uint8Array(16));
      const newWrappingKey = await this.cryptoEngine.deriveWrappingKey(
        newPin,
        newSalt.buffer,
      );

      const newWrappedKey = await window.crypto.subtle.wrapKey(
        'raw',
        combinedKeyHandle,
        newWrappingKey,
        'AES-KW',
      );

      await this.indexedDbService.saveWrappedKey(
        this.currentOrgId,
        newWrappedKey,
        newSalt.buffer,
      );
      return true;
    } catch (e) {
      console.error('Failed to change PIN', e);
      return false; 
    }
  }

  async isVaultInitialized(): Promise<boolean> {
    if (!this.currentOrgId) return false;
    const data = await this.indexedDbService.getWrappedKeyData(this.currentOrgId);
    return !!data;
  }

  async unlockVault(pin: string): Promise<boolean> {
    try {
      if (!this.currentOrgId) return false;
      const data = await this.indexedDbService.getWrappedKeyData(this.currentOrgId);
      if (!data) return false;

      const { wrappedKey, salt } = data;
      const wrappingKey = await this.cryptoEngine.deriveWrappingKey(pin, salt);

      const combinedKeyHandle = await window.crypto.subtle.unwrapKey(
        'raw',
        wrappedKey,
        wrappingKey,
        'AES-KW',
        { name: 'HMAC', hash: 'SHA-512' },
        true,
        ['sign'],
      );

      const combinedBytes = await window.crypto.subtle.exportKey(
        'raw',
        combinedKeyHandle,
      );

      if (combinedBytes.byteLength !== 64) {
        throw new Error('Vault data format mismatch');
      }

      await this.persistSession(combinedBytes);

      const buffer = new Uint8Array(combinedBytes);
      const masterKeyBytes = buffer.slice(0, 32);
      const searchSaltBytes = buffer.slice(32, 64);

      this._masterKey = await this.cryptoEngine.importMasterKey(masterKeyBytes, false);
      this._searchSalt = await this.cryptoEngine.importSearchSalt(searchSaltBytes);

      this.lockedSubject.next(false);
      return true;
    } catch (error) {
      console.error('Unlock failed', error);
      return false;
    }
  }

  // --- Session Persistence (Zero-Knowledge) ---

  private async persistSession(keyBytes: any): Promise<void> {
    try {
      const sessionSeed = window.crypto.getRandomValues(new Uint8Array(32)); 
      window.name = this.cryptoEngine.arrayBufferToBase64(sessionSeed);

      const sessionKey = await window.crypto.subtle.importKey(
        'raw',
        sessionSeed as any,
        { name: 'AES-GCM' },
        false,
        ['encrypt'],
      );

      const iv = window.crypto.getRandomValues(new Uint8Array(12));
      const ciphertext = await window.crypto.subtle.encrypt(
        { name: 'AES-GCM', iv: iv as any },
        sessionKey,
        keyBytes as any,
      );

      const encryptedData = {
        iv: this.cryptoEngine.arrayBufferToBase64(iv),
        data: this.cryptoEngine.arrayBufferToBase64(new Uint8Array(ciphertext)),
      };
      sessionStorage.setItem('medease_vault_cache', JSON.stringify(encryptedData));
    } catch (e) {
      console.error('Failed to persist session', e);
      this.lockVault();
    }
  }

  private async tryRestoreSession(): Promise<void> {
    try {
      const storedCache = sessionStorage.getItem('medease_vault_cache');
      const storedSeed = window.name;

      if (!storedCache || !storedSeed) {
        return;
      }

      const sessionSeed = this.cryptoEngine.base64ToArrayBuffer(storedSeed);
      if (sessionSeed.byteLength !== 32) return;

      const sessionKey = await window.crypto.subtle.importKey(
        'raw',
        sessionSeed as any,
        { name: 'AES-GCM' },
        false,
        ['decrypt'],
      );

      const { iv, data } = JSON.parse(storedCache);
      const ivBytes = this.cryptoEngine.base64ToArrayBuffer(iv);
      const ciphertextBytes = this.cryptoEngine.base64ToArrayBuffer(data);

      const combinedBytes = await window.crypto.subtle.decrypt(
        { name: 'AES-GCM', iv: ivBytes as any },
        sessionKey,
        ciphertextBytes as any,
      );

      if (combinedBytes.byteLength !== 64) {
        console.warn('Invalid session key length. Clearing session.');
        this.lockVault();
        return;
      }

      const buffer = new Uint8Array(combinedBytes);
      const masterKeyBytes = buffer.slice(0, 32);
      const searchSaltBytes = buffer.slice(32, 64);

      this._masterKey = await this.cryptoEngine.importMasterKey(masterKeyBytes, false);
      this._searchSalt = await this.cryptoEngine.importSearchSalt(searchSaltBytes);

      this.lockedSubject.next(false);
    } catch (e) {
      this.lockVault();
    }
  }

  async resetVault(): Promise<void> {
    if (this.currentOrgId) {
      await this.indexedDbService.clearStorage(this.currentOrgId);
    }
    this._masterKey = null;
    this._searchSalt = null;
    window.name = '';
    sessionStorage.removeItem('medease_vault_cache');
    this.lockedSubject.next(false);
  }

  lockVault(showLockUI: boolean = true): void {
    this._masterKey = null;
    this._searchSalt = null;
    if (showLockUI && !this.isBypassVault) {
      this.lockedSubject.next(true);
    } else {
      this.lockedSubject.next(false);
    }
    window.name = '';
    sessionStorage.removeItem('medease_vault_cache');
  }

  triggerVaultSetup() {
    if (this.isBypassVault) return;
    this.showVaultSetupSubject.next(true);
  }

  closeVaultSetup() {
    this.showVaultSetupSubject.next(false);
  }

  public setLockedState(isLocked: boolean) {
    if (isLocked && this.isBypassVault) return;
    this.lockedSubject.next(isLocked);
  }
}
